import { nanoid } from 'nanoid'
import { AgentAttachmentType, type AgentAttachment } from '@multiplayer-app/ai-agent-types'

type BaseContextParams = {
  id?: string
  /**
   * Human-friendly label used in the UI chip.
   */
  name?: string
  /**
   * Optional URL for the attachment (also used as a default source.url).
   */
  url?: string
  /**
   * Override capture time; otherwise set to now (ISO).
   */
  capturedAt?: string
  /**
   * Optional metadata hints for privacy / retention policy.
   */
  security?: {
    containsPII?: boolean
    redactionsApplied?: string[]
  }
  source?: {
    app?: string
    url?: string
    route?: string
    domPath?: string
  }
  /**
   * Optional locator to re-find/highlight the original UI element later (e.g. on attachment click).
   */
  target?:
    | {
        type: 'text'
        exact: string
        prefix?: string
        suffix?: string
        domPath?: string
        startOffset?: number
        endOffset?: number
      }
    | {
        type: 'dom'
        domPath: string
      }
    | {
        type: 'form'
        formId?: string
        fieldName?: string
        domPath?: string
      }
}

export type WebSnippetContextParams = BaseContextParams & {
  kind: 'webSnippet'
  selectedText: string
  title?: string
}

export type FormSnapshotContextParams = BaseContextParams & {
  kind: 'formSnapshot'
  formId?: string
  formName?: string
  fields: Array<{
    name: string
    label?: string
    value: string
    inputType?: 'text' | 'textarea' | 'number' | 'select' | 'radio' | 'checkbox' | 'date' | 'email' | 'tel'
    options?: Array<{ value: string; label: string }>
    domPath?: string
  }>
}

export type FormFieldContextParams = BaseContextParams & {
  kind: 'formField'
  formId?: string
  fieldName: string
  fieldLabel?: string
  value: string
  inputType?: 'text' | 'textarea' | 'number' | 'select' | 'radio' | 'checkbox' | 'date' | 'email' | 'tel'
  options?: Array<{ value: string; label: string }>
  /**
   * Optional selector pointing at the actual input element in the DOM.
   */
  domPath?: string
}

export type CustomContextParams = BaseContextParams & {
  /**
   * Any non-built-in kind.
   */
  kind: string
  title?: string
  summary?: string
  data?: Record<string, unknown>
}

export type ContextAttachmentParams =
  | WebSnippetContextParams
  | FormSnapshotContextParams
  | FormFieldContextParams
  | CustomContextParams

export type PageContextParams = BaseContextParams & {
  kind: 'pageContext'
  /**
   * Optional short title shown in the UI / logs.
   * Defaults to `document.title` when available.
   */
  title?: string
  /**
   * Optional short summary string.
   */
  summary?: string
  /**
   * Arbitrary structured page context.
   */
  data?: Record<string, unknown>
}

/**
 * Build a `AgentAttachmentType.Context` attachment, filling required boilerplate:
 * `id`, `type`, `metadata.schemaVersion`, `metadata.capturedAt`, and default `source.url`.
 *
 * This intentionally keeps the API small and predictable; callers provide only the meaningful fields.
 */
export const createContextAttachment = (params: ContextAttachmentParams): AgentAttachment => {
  const { id, name, url, capturedAt, source, security, target, kind, ...rest } = params as any

  const resolvedUrl = url ?? source?.url
  const resolvedSource =
    source || resolvedUrl ? { ...(source ?? {}), ...(resolvedUrl ? { url: resolvedUrl } : {}) } : undefined

  const defaultContainsPII = kind === 'webSnippet' || kind === 'formSnapshot' || kind === 'formField' ? true : undefined

  const resolvedSecurity =
    security ||
    (defaultContainsPII !== undefined
      ? {
          containsPII: defaultContainsPII,
          redactionsApplied: []
        }
      : undefined)

  const defaultName =
    typeof name === 'string' && name.trim().length
      ? name.trim()
      : kind === 'webSnippet'
        ? rest?.title
          ? `Selection: ${String(rest.title)}`
          : 'Selection'
        : kind === 'formField'
          ? 'Form field'
          : kind === 'formSnapshot'
            ? 'Form snapshot'
            : 'Context'

  return {
    id: id ?? nanoid(),
    type: AgentAttachmentType.Context,
    name: defaultName,
    ...(resolvedUrl ? { url: resolvedUrl } : {}),
    metadata: {
      schemaVersion: 1,
      kind,
      capturedAt: capturedAt ?? new Date().toISOString(),
      ...(resolvedSource ? { source: resolvedSource } : {}),
      ...(resolvedSecurity ? { security: resolvedSecurity } : {}),
      ...(target ? { target } : {}),
      ...rest
    }
  }
}

const looksSensitiveUrl = (url: string): boolean => {
  try {
    const u = new URL(url)
    const hay = `${u.searchParams.toString()} ${u.hash}`.toLowerCase()
    return /(token|key|auth|session|code|password|secret|email)/.test(hay)
  } catch {
    return /(token|key|auth|session|code|password|secret|email)/.test(url.toLowerCase())
  }
}

export type CreatePageContextAttachmentOptions = {
  /**
   * Override the URL; defaults to window.location.href.
   */
  url?: string
  /**
   * Include extra data fields in `metadata.data`.
   */
  extra?: Record<string, unknown>
  /**
   * Include navigator.userAgent. Defaults to false (often noisy + fingerprinting).
   */
  includeUserAgent?: boolean
  /**
   * Include document.referrer. Defaults to true.
   */
  includeReferrer?: boolean
  /**
   * Include viewport info. Defaults to true.
   */
  includeViewport?: boolean
  /**
   * Include document.title. Defaults to true.
   */
  includeTitle?: boolean
  /**
   * Override the attachment chip label.
   */
  name?: string
}

/**
 * Creates a stable "ambient" context attachment describing the current page.
 * Intended to be used as a default attachment and refreshed on route changes.
 */
export const createPageContextAttachment = (
  options: CreatePageContextAttachmentOptions = {}
): AgentAttachment | undefined => {
  if (typeof window === 'undefined') return
  const href = options.url ?? window.location?.href
  if (!href) return

  const includeTitle = options.includeTitle !== false
  const includeReferrer = options.includeReferrer !== false
  const includeViewport = options.includeViewport !== false
  const includeUserAgent = options.includeUserAgent === true

  const title = includeTitle && typeof document !== 'undefined' ? document.title : undefined
  const referrer = includeReferrer && typeof document !== 'undefined' ? document.referrer : undefined

  let parsed: URL | undefined
  try {
    parsed = new URL(href)
  } catch {}

  const data: Record<string, unknown> = {
    url: href,
    ...(parsed
      ? {
          origin: parsed.origin,
          pathname: parsed.pathname,
          search: parsed.search,
          hash: parsed.hash
        }
      : {}),
    ...(includeTitle && title ? { title } : {}),
    ...(includeReferrer && referrer ? { referrer } : {}),
    ...(includeUserAgent && typeof navigator !== 'undefined' ? { userAgent: navigator.userAgent } : {}),
    ...(typeof navigator !== 'undefined' && navigator.language ? { language: navigator.language } : {}),
    ...(typeof Intl !== 'undefined'
      ? {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: Intl.DateTimeFormat().resolvedOptions().locale
        }
      : {}),
    ...(includeViewport && typeof window !== 'undefined'
      ? {
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio
          }
        }
      : {}),
    ...(options.extra ? options.extra : {})
  }

  const containsPII = looksSensitiveUrl(href) ? true : undefined

  return createContextAttachment({
    kind: 'pageContext',
    name: options.name ?? 'Page context',
    url: href,
    title,
    data,
    summary: parsed ? `${parsed.pathname}${parsed.search}${parsed.hash}` : undefined,
    source: {
      app: 'browser',
      url: href,
      route: parsed ? `${parsed.pathname}${parsed.search}${parsed.hash}` : undefined
    },
    ...(containsPII ? { security: { containsPII: true, redactionsApplied: [] } } : {})
  })
}

/**
 * Capture a robust-ish text selection descriptor (text quote selector).
 * This is not perfect, but is far more stable than raw Range offsets.
 */
export const captureTextSelectionTarget = ():
  | { selectedText: string; target: BaseContextParams['target'] }
  | undefined => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const sel = window.getSelection?.()
  if (!sel || sel.rangeCount === 0) return
  const range = sel.getRangeAt(0)
  const selectedText = sel.toString().trim()
  if (!selectedText) return

  const containerEl =
    (range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? (range.commonAncestorContainer as Element)
      : range.commonAncestorContainer.parentElement) ?? undefined

  const domPath = containerEl ? getDomPath(containerEl) : undefined

  const containerText = containerEl?.textContent ?? ''
  const index = containerText.indexOf(selectedText)
  const startOffset = index >= 0 ? index : undefined
  const endOffset = index >= 0 ? index + selectedText.length : undefined

  const prefix = index >= 0 ? containerText.slice(Math.max(0, index - 80), index).trim() : undefined
  const suffix =
    index >= 0 ? containerText.slice(index + selectedText.length, index + selectedText.length + 80).trim() : undefined

  return {
    selectedText,
    target: {
      type: 'text',
      exact: selectedText,
      ...(prefix ? { prefix } : {}),
      ...(suffix ? { suffix } : {}),
      ...(domPath ? { domPath } : {}),
      ...(startOffset !== undefined ? { startOffset } : {}),
      ...(endOffset !== undefined ? { endOffset } : {})
    }
  }
}

const getDomPath = (el: Element): string => {
  // Prefer a stable id if present.
  if (el.id) return `#${cssEscape(el.id)}`
  const parts: string[] = []
  let node: Element | null = el
  while (node && node.nodeType === 1 && parts.length < 6) {
    const tag = node.tagName.toLowerCase()
    if (node.id) {
      parts.unshift(`#${cssEscape(node.id)}`)
      break
    }
    const parent: Element | null = node.parentElement
    if (!parent) {
      parts.unshift(tag)
      break
    }
    const siblings = Array.from(parent.children).filter((c: Element) => c.tagName === node!.tagName)
    const idx = siblings.indexOf(node) + 1
    parts.unshift(`${tag}:nth-of-type(${idx})`)
    node = parent
  }
  return parts.join(' > ')
}

const cssEscape = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '\\$&')
